import { GlobalSearchDialog } from "@megarax/home";
import { useEffect, useRef, useState } from "react";
import { MdMenu, MdSearch } from "react-icons/md";
import { Link, useLocation, useSearchParams } from "react-router-dom-v5-compat";

import { mobileNavBarHeight, navAccent } from "./navConstants";
import { MobileNavShortcut } from "./mobileAppShortcuts";

type Props = {
  shortcuts: MobileNavShortcut[];
  onMenuClick: () => void;
  barRef?: React.RefObject<HTMLDivElement | null>;
};

const reservedBarWidth = 120;
const shortcutSlotWidth = 49;

export const MobileNavCollapsedBar: React.FC<Props> = ({ shortcuts, onMenuClick, barRef }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const shortcutsContainerRef = useRef<HTMLDivElement>(null);
  const [visibleShortcuts, setVisibleShortcuts] = useState<MobileNavShortcut[]>(shortcuts);

  useEffect(() => {
    const updateVisibleShortcuts = () => {
      const containerWidth = shortcutsContainerRef.current?.offsetWidth ?? window.innerWidth;
      const maxCount = Math.max(1, Math.floor((containerWidth - reservedBarWidth) / shortcutSlotWidth));
      setVisibleShortcuts(shortcuts.slice(0, maxCount));
    };

    updateVisibleShortcuts();
    window.addEventListener("resize", updateVisibleShortcuts);
    return () => window.removeEventListener("resize", updateVisibleShortcuts);
  }, [shortcuts]);

  return (
    <div
      ref={barRef}
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        height: mobileNavBarHeight,
        padding: "0.375rem 0.75rem",
        background: "white",
        boxShadow: "0 -1px 4px rgba(0,0,0,0.08)",
        flexShrink: 0,
      }}
    >
      <div
        ref={shortcutsContainerRef}
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {visibleShortcuts.map((shortcut) => {
          const isActive = shortcut.isActiveAt(location.pathname, searchParams);
          const linkColor = isActive ? shortcut.color : navAccent;

          const linkCss = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2.8125rem",
            height: "1.9375rem",
            borderRadius: "0.375rem",
            color: linkColor,
            opacity: isActive ? 1 : 0.5,
            textDecoration: "none",
            flexShrink: 0,
          };

          const icon = <shortcut.Icon css={{ width: "1rem", height: "1rem" }} />;

          if (shortcut.isExternal) {
            return (
              <a key={shortcut.key} href={shortcut.to} css={linkCss}>
                {icon}
              </a>
            );
          }

          return (
            <Link key={shortcut.key} to={shortcut.to} css={linkCss}>
              {icon}
            </Link>
          );
        })}
      </div>

      <GlobalSearchDialog
        renderTrigger={(onOpen) => (
          <button
            type="button"
            onClick={onOpen}
            css={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2rem",
              height: "2rem",
              border: "none",
              borderRadius: "0.375rem",
              background: "rgba(0,0,0,0.06)",
              color: navAccent,
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            <MdSearch css={{ width: "1rem", height: "1rem" }} />
          </button>
        )}
      />

      <button
        type="button"
        onClick={onMenuClick}
        css={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "2rem",
          height: "2rem",
          border: "none",
          borderRadius: "0.375rem",
          background: navAccent,
          color: "white",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <MdMenu css={{ width: "1rem", height: "1rem" }} />
      </button>
    </div>
  );
};
